import { PageHero, LocalBusinessSchema } from '@components/common';
import { CALENDLY_URL } from '@constants';
import { Shield, Target, Users, Clock, MapPin } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function ReadingPage() {
  return (
    <>
      <LocalBusinessSchema city="Reading" latitude={51.4543} longitude={-0.9781} />
      <PageHero
        title="Fractional CISO Services in Reading"
        subtitle="Cybersecurity leadership for the Thames Valley tech corridor"
        eyebrow="Reading"
        seoTitle="Fractional CISO Reading | The CISO Network"
        seoDescription="Expert fractional CISO services for Reading businesses. Access senior cybersecurity leadership for the Thames Valley's enterprise tech sector."
      />

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Securing the Thames Valley's Enterprise Tech Hub
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Reading sits at the heart of the UK's most concentrated enterprise technology
                  corridor, with the Thames Valley hosting more multinational tech headquarters than
                  anywhere outside London. The town combines this global corporate presence with a
                  thriving ecosystem of scale-ups, consultancies, and specialist technology firms
                  serving enterprise clients worldwide.
                </p>
                <p>
                  Thames Valley businesses face demanding cybersecurity challenges. Enterprise
                  software and SaaS companies must meet rigorous security standards to win corporate
                  contracts. The region's prominence in telecommunications, data centres, and
                  critical infrastructure attracts sophisticated threats. Meanwhile, professional
                  services firms handle sensitive client data requiring robust protection and
                  regulatory compliance.
                </p>
                <p>
                  Many Reading businesses are B2B technology companies serving FTSE 100 and global
                  clients. These organisations need demonstrable C-level security leadership to
                  satisfy procurement requirements and pass vendor assessments - but often find
                  full-time CISOs prohibitively expensive for their stage. Our fractional CISO
                  service provides Thames Valley businesses with executive security expertise that
                  meets enterprise expectations.
                </p>
                <p>
                  Reading sits at the heart of the M4 technology corridor, hosting major telecoms,
                  software, and enterprise technology companies alongside a growing ecosystem of
                  scale-ups and professional services firms. The region's businesses face complex
                  supply chain security challenges and demanding enterprise customer requirements. A
                  fractional CISO with enterprise technology experience is ideally suited to this
                  environment.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Your Account Manager
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Your account manager understands the unique demands of Thames Valley
                      enterprise technology businesses.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Clock className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      M4 Corridor Coverage
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Supporting businesses across Reading, Bracknell, Wokingham, and the wider
                      Thames Valley.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Why Reading Businesses Choose Fractional CISOs
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Enterprise-grade security leadership for the Thames Valley
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Enterprise Ready',
                description:
                  'Security programmes that satisfy FTSE 100 vendor assessments and procurement.',
              },
              {
                icon: Target,
                title: 'Cost Effective',
                description:
                  'Access senior CISO expertise without Thames Valley premium salary costs.',
              },
              {
                icon: Users,
                title: 'B2B Specialists',
                description:
                  'CISOs who understand the unique security demands of B2B and SaaS businesses.',
              },
              {
                icon: Clock,
                title: 'Flexible Engagement',
                description:
                  'Scale security leadership as your Reading business wins larger contracts.',
              },
            ].map((benefit, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <benefit.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO Services in Reading - FAQs"
        subtitle="Common questions from Reading and Thames Valley businesses"
        items={[
          {
            id: 'reading-1',
            question: 'How does a fractional CISO work with businesses in Reading?',
            answer:
              'Our fractional CISOs work with organisations across the UK, including Reading. They can attend meetings on-site or work remotely, depending on what suits your business. We match you with a CISO who understands the security challenges facing telecoms, enterprise technology, and professional services companies.',
          },
          {
            id: 'reading-2',
            question: 'What size of business benefits from a fractional CISO in Reading?',
            answer:
              'Fractional CISO services are ideal for growing businesses that need strategic security leadership but are not yet ready for a full-time executive hire. This includes Reading startups, scale-ups, and mid-sized companies that are winning larger contracts or facing regulatory requirements.',
          },
          {
            id: 'reading-3',
            question: 'What industries do your CISOs cover in Reading?',
            answer:
              "Our network includes CISOs with experience in telecoms, enterprise technology, and professional services. We match you with someone who understands your sector's specific regulatory environment and threat landscape, whether you are navigating FCA requirements, NHS Digital standards, or customer security questionnaires.",
          },
          {
            id: 'reading-4',
            question: 'What is the first thing a fractional CISO does when they start?',
            answer:
              'Every engagement begins with discovery. Your CISO assesses your current security posture, understands your business priorities, and develops a tailored roadmap. This typically delivers actionable insights within the first 30 days, with quick wins identified early.',
          },
          {
            id: 'reading-5',
            question: 'Can a fractional CISO help with board reporting and governance?',
            answer:
              'Yes. A core part of the fractional CISO role is translating technical security risks into business language for boards and leadership teams. Our CISOs build reporting frameworks, present to boards, and establish governance structures that satisfy regulators and investors.',
          },
        ]}
      />

      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Secure Your Reading Business?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Speak with our team today and discover how a fractional CISO can help you win enterprise
            clients.
          </p>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
            Book a Call
          </a>
        </div>
      </section>
    </>
  );
}
